import _ from 'lodash'
import React from 'react'
import styled from 'styled-components'
import { getInterpolateColor } from '../../../utils'
import frontBg from '../../../asserts/images/dial-plat.png'

const Container = styled.div`
  position: relative;
  svg {
    position: absolute;
    top: 0;
  }
  .progress-circle {
    .progress-circle-trail {
      stroke-dashoffset: 0;
      transform-origin: 0px 0px;
      transition: stroke-dashoffset 0.3s ease 0s, stroke-dasharray 0.3s ease 0s,
        stroke 0.3s ease 0s, strokeWidth 0.06s ease 0.3s, opacity 0.3s ease 0s;
      fill-opacity: 0;
      stroke: #0000000a;
    }
    .progress-circle-path {
      transform-origin: 0px 0px;
      transition: stroke-dashoffset 0s ease 0s, stroke-dasharray 0s ease 0s,
        stroke ease 0s, strokeWidth ease 0.3s, opacity ease 0s;
      fill-opacity: 0;
    }
  }
  .progress-text {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    margin: 0;
    padding: 0;
    color: #000000d9;
    font-size: 1.5rem;
    line-height: 1;
    white-space: normal;
    text-align: center;
    transform: translate(-50%, -50%);
  }
  .back-range {
    width: 100%;
    padding-top: 100%;
    background: #e9ebf3;
    box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.1) inset;
    border-radius: 100%;
    &:before {
      content: '';
      display: block;
      background: #ffffff;
      width: 80%;
      padding-top: 80%;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      border-radius: 100%;
    }
  }
  .front-bg {
    background-size: 94% 94%;
    background-position: center;
    background-image: url(${frontBg});
    background-repeat: no-repeat;
    position: absolute;
    width: 100%;
    padding-top: 100%;
    top: 0;
    right: 0;
  }
`

const colorDictionary = {
  green: ['#A4C243', '#A7C33E', '#A7C33F', '#74AC90', '#3991E8'],
  orange: ['#F4C50A', '#F3C407', '#F2C407', '#CCC326', '#A4C243'],
  blue: ['#3991E8', '#549EE6', '#6FAAE5', '#8BB7E2', '#BACCDD']
}

const getRingColors = (ringIndex) => {
  if (ringIndex === 0) {
    return colorDictionary.green
  } else if (ringIndex === 1) {
    return colorDictionary.orange
  } else {
    return colorDictionary.blue
  }
}

const getRingProgress = (percent, ringIndex) => {
  const maxPercentPerRing = 100
  const ringStart = ringIndex * maxPercentPerRing
  const ringEnd = (ringIndex + 1) * maxPercentPerRing
  
  if (percent <= ringStart) {
    return 0
  } else if (percent >= ringEnd) {
    return 100
  } else {
    return (percent - ringStart) * 100 / maxPercentPerRing
  }
}

export default function HSDRing(props) {
  const { percent, showProgressText = false } = props
  const numRings = Math.ceil(percent / 100)
  const rings = []

  for (let i = 0; i < numRings; i++) {
    const ringProgress = getRingProgress(percent, i)
    const ringColors = getRingColors(i)
    const strokeWidth = 8 // Consistent stroke width for all rings
    const radius = 46 - (i * (strokeWidth + 2)) // Calculate radius to ensure rings are separated
    const { points, colors } = getPathPoints(ringProgress, ringColors, radius)
    rings.push({ points, colors, strokeWidth, radius, ringIndex: i })
  }

  // Define solid colors for each ring for better visibility
  const solidColors = [
    '#A4C243', // Green for first ring (0-100%)
    '#FF7F00', // Orange for second ring (100-200%)
    '#3991E8'  // Blue for third ring (200%+)
  ]

  return (
    <Container>
      <div className="back-range"></div>
      <svg
        className="progress-circle"
        viewBox="0 0 100 100"
        role="presentation"
      >
        {rings.map((ring, ringIndex) => {
          const ringColor = solidColors[ringIndex] || solidColors[solidColors.length - 1]
          return (
            <React.Fragment key={ringIndex}>
              {ring.points.map((point, index) => {
                const [[x1, y1], [x2, y2]] = point
                return (
                  <path
                    key={`${ringIndex}-${index}`}
                    d={`M${x1} ${y1} A${ring.radius} ${ring.radius} 0 0 1 ${x2} ${y2}`}
                    fill="none"
                    strokeWidth={ring.strokeWidth}
                    stroke={ringColor}
                    strokeLinecap="round"
                  />
                )
              })}
            </React.Fragment>
          )
        })}
      </svg>
      <div className="front-bg"></div>
      {showProgressText && (
        <span className="progress-text">
          {_.isNumber(percent) && (
            <span>
              <strong>{percent}</strong>%
            </span>
          )}
        </span>
      )}
    </Container>
  )
}

function getPathPoints(percent, colors, radius = 46) {
  let _colors = _.cloneDeep(colors)
  // Calculate positions dynamically based on radius
  const positions = [
      [50, 50 - radius],
      [50 + radius, 50],
      [50, 50 + radius],
      [50 - radius, 50]
    ],
    r = radius
  const count = Math.ceil(percent / 25)
  let points = []
  for (let i = 0; i < count; i++) {
    const diff = percent - 25 * i
    let _point = positions[(i + 1) % 4]
    if (diff < 25) {
      const segmentDeg = ((Math.PI / 2) * diff) / 25
      const sin = Math.floor(Math.sin(segmentDeg) * r),
        cos = Math.floor(Math.cos(segmentDeg) * r)
      const _i = i % 4
      if (_i % 2 === 0) {
        _point = calculatePoint(sin, cos, _i, radius)
      } else {
        _point = calculatePoint(cos, sin, _i, radius)
      }
    }
    points.push([positions[i % 4], _point])
  }

  const len = points.length
  if (len > 4) {
    const addColors = [...new Array(len - 5).fill(colors[1])]
    _colors.splice(3, 0, colors[3])
    _colors.splice(2, 0, colors[2])
    _colors.splice(1, 0, ...addColors)
  }

  return {
    points,
    colors: _colors
  }
}

function calculatePoint(sin, cos, i, radius) {
  let _x, _y
  switch (i) {
    case 0: // Top to right quadrant
      _x = 50 + sin
      _y = 50 - radius + (radius - cos)
      break
    case 1: // Right to bottom quadrant
      _x = 50 + radius - (radius - cos)
      _y = 50 + sin
      break
    case 2: // Bottom to left quadrant
      _x = 50 - sin
      _y = 50 + radius - (radius - cos)
      break
    case 3: // Left to top quadrant
      _x = 50 - radius + (radius - cos)
      _y = 50 - sin
      break
    default:
      return undefined
  }
  return [_x, _y]
}
